/**
 * RelationshipDomainService
 *
 * Centralizes relationship business rules and operations.
 * This service encapsulates the logic for creating and validating relationships
 * in the family tree, bridging input validation and graph validation layers.
 *
 * **Validation Boundaries:**
 * - Input validation (lib/validation/): Form-level, standalone field validation, no tree context
 * - Domain validation (this service): Basic relationship business rules, simple context
 * - Graph validation (features/dashboard/validation/): Complex tree-context rules (cycles, etc.)
 *
 * **Current Scope:**
 * - Parent-child relationships (father, mother, son, daughter)
 * - Partner/spouse relationships
 * - Sibling relationships (brother, sister) - with shared-parent logic
 *
 * @module services/RelationshipDomainService
 */

import type { RelationshipType } from '@/lib/validation/relationship.schema';

// ==================== TYPE DEFINITIONS ====================

/**
 * Gender type for persons
 */
export type Gender = 'male' | 'female' | 'other';

/**
 * Relationship direction for parent-child relationships
 */
export type RelationshipDirection = 'parent_to_child' | 'child_to_parent' | 'bidirectional';

/**
 * Relationship metadata for business logic
 */
export interface RelationshipMetadata {
  type: RelationshipType;
  direction: RelationshipDirection;
  requiredGender?: Gender; // Required gender for the NEW person being added
  allowedGenders?: Gender[]; // Allowed genders if flexible (e.g., spouse)
  requiresParents?: boolean; // For sibling relationships
}

/**
 * Relationship creation parameters
 */
export interface RelationshipCreationParams {
  relationshipType: RelationshipType;
  sourcePersonId: string; // The existing person
  sourcePersonGender?: Gender; // Optional gender of source person (for spouse gender rules)
  targetPersonId: string; // The new person being added
  targetPersonGender: Gender; // Gender of the new person
  treeId: string;
  parentIds?: string[]; // For sibling relationships - inherited parent IDs
}

/**
 * Relationship creation result
 */
export interface RelationshipCreationResult {
  success: boolean;
  relationshipsToCreate: Array<{
    personAId: string;
    personBId: string;
    relationshipType: 'parent_child' | 'spouse';
  }>;
  errors?: string[];
}

// ==================== RELATIONSHIP DOMAIN SERVICE ====================

/**
 * RelationshipDomainService
 *
 * Provides centralized business logic for relationship operations.
 * Handles relationship type mapping, directionality, and creation planning.
 */
export class RelationshipDomainService {
  /**
   * Get metadata for a relationship type
   * Defines business rules for each relationship type
   */
  public getRelationshipMetadata(relationshipType: RelationshipType): RelationshipMetadata {
    switch (relationshipType) {
      case 'father':
        return {
          type: 'father',
          direction: 'parent_to_child',
          requiredGender: 'male',
        };

      case 'mother':
        return {
          type: 'mother',
          direction: 'parent_to_child',
          requiredGender: 'female',
        };

      case 'son':
        return {
          type: 'son',
          direction: 'child_to_parent',
          requiredGender: 'male',
        };

      case 'daughter':
        return {
          type: 'daughter',
          direction: 'child_to_parent',
          requiredGender: 'female',
        };

      case 'brother':
        return {
          type: 'brother',
          direction: 'bidirectional',
          requiredGender: 'male',
          requiresParents: true, // Siblings must share at least one parent
        };

      case 'sister':
        return {
          type: 'sister',
          direction: 'bidirectional',
          requiredGender: 'female',
          requiresParents: true, // Siblings must share at least one parent
        };

      case 'spouse':
        return {
          type: 'spouse',
          direction: 'bidirectional',
          allowedGenders: ['male', 'female', 'other'], // Flexible based on source person
        };

      default:
        // Exhaustive check - TypeScript will error if we miss a case
        const _exhaustive: never = relationshipType;
        throw new Error(`Unknown relationship type: ${relationshipType}`);
    }
  }

  /**
   * Determine allowed genders for spouse based on source person's gender
   * Implements product rule: prefer opposite gender for spouse
   */
  public getAllowedSpouseGenders(sourcePersonGender?: Gender): Gender[] {
    if (!sourcePersonGender || sourcePersonGender === 'other') {
      // If source gender is unknown or 'other', allow all genders
      return ['male', 'female'];
    }

    if (sourcePersonGender === 'male') {
      return ['female'];
    }

    if (sourcePersonGender === 'female') {
      return ['male'];
    }

    // Fallback (shouldn't reach here)
    return ['male', 'female'];
  }

  /**
   * Validate gender constraint for a relationship
   * Checks if the target person's gender matches the relationship requirements
   */
  public validateGenderConstraint(
    relationshipType: RelationshipType,
    targetPersonGender: Gender,
    sourcePersonGender?: Gender
  ): { valid: boolean; error?: string } {
    const metadata = this.getRelationshipMetadata(relationshipType);

    // Check required gender
    if (metadata.requiredGender && targetPersonGender !== metadata.requiredGender) {
      return {
        valid: false,
        error: `A ${relationshipType} must be ${metadata.requiredGender}, but gender is ${targetPersonGender}`,
      };
    }

    // Check allowed genders for spouse
    if (relationshipType === 'spouse') {
      const allowedGenders = this.getAllowedSpouseGenders(sourcePersonGender);
      if (!allowedGenders.includes(targetPersonGender)) {
        return {
          valid: false,
          error: `Spouse gender ${targetPersonGender} is not compatible with source person gender ${sourcePersonGender}`,
        };
      }
    }

    return { valid: true };
  }

  /**
   * Map relationship type to database relationship type and determine directionality
   * Returns person_a and person_b IDs based on relationship semantics
   */
  public mapRelationshipToDatabase(
    relationshipType: RelationshipType,
    sourcePersonId: string,
    targetPersonId: string
  ): {
    personAId: string;
    personBId: string;
    dbRelationshipType: 'parent_child' | 'spouse';
  } {
    const metadata = this.getRelationshipMetadata(relationshipType);

    switch (metadata.direction) {
      case 'parent_to_child':
        // New person is parent, source person is child
        // For parent_child: person_a is parent, person_b is child
        return {
          personAId: targetPersonId, // new parent
          personBId: sourcePersonId, // existing child
          dbRelationshipType: 'parent_child',
        };

      case 'child_to_parent':
        // Source person is parent, new person is child
        // For parent_child: person_a is parent, person_b is child
        return {
          personAId: sourcePersonId, // existing parent
          personBId: targetPersonId, // new child
          dbRelationshipType: 'parent_child',
        };

      case 'bidirectional':
        // Spouse or sibling - order doesn't matter for spouse
        // For siblings, we'll create parent-child relationships with shared parents
        return {
          personAId: sourcePersonId,
          personBId: targetPersonId,
          dbRelationshipType: 'spouse',
        };

      default:
        throw new Error(`Unknown relationship direction: ${metadata.direction}`);
    }
  }

  /**
   * Plan relationship creation
   * Determines what relationships need to be created in the database
   * Handles complex cases like siblings (multiple parent-child relationships)
   */
  public planRelationshipCreation(
    params: RelationshipCreationParams
  ): RelationshipCreationResult {
    const errors: string[] = [];

    // Validate gender constraint
    const genderValidation = this.validateGenderConstraint(
      params.relationshipType,
      params.targetPersonGender,
      params.sourcePersonGender
    );

    if (!genderValidation.valid) {
      return {
        success: false,
        errors: [genderValidation.error!],
        relationshipsToCreate: [],
      };
    }

    const metadata = this.getRelationshipMetadata(params.relationshipType);
    const relationshipsToCreate: Array<{
      personAId: string;
      personBId: string;
      relationshipType: 'parent_child' | 'spouse';
    }> = [];

    // Handle sibling relationships - create parent-child relationships with shared parents
    if (metadata.requiresParents && (params.relationshipType === 'brother' || params.relationshipType === 'sister')) {
      if (!params.parentIds || params.parentIds.length === 0) {
        return {
          success: false,
          errors: ['Sibling relationships require at least one shared parent'],
          relationshipsToCreate: [],
        };
      }

      // Create parent-child relationship for each shared parent
      for (const parentId of params.parentIds) {
        relationshipsToCreate.push({
          personAId: parentId, // parent
          personBId: params.targetPersonId, // new sibling
          relationshipType: 'parent_child',
        });
      }
    } else {
      // Standard relationship (parent, child, spouse)
      const mapping = this.mapRelationshipToDatabase(
        params.relationshipType,
        params.sourcePersonId,
        params.targetPersonId
      );

      relationshipsToCreate.push({
        personAId: mapping.personAId,
        personBId: mapping.personBId,
        relationshipType: mapping.dbRelationshipType,
      });
    }

    return {
      success: true,
      relationshipsToCreate,
    };
  }

  /**
   * Check if a relationship type requires pre-existing parents
   * Used for validation before opening add person modal
   */
  public requiresParents(relationshipType: RelationshipType): boolean {
    const metadata = this.getRelationshipMetadata(relationshipType);
    return metadata.requiresParents === true;
  }

  /**
   * Get the required gender for a relationship type
   * Returns undefined if gender is flexible
   */
  public getRequiredGender(relationshipType: RelationshipType): Gender | undefined {
    const metadata = this.getRelationshipMetadata(relationshipType);
    return metadata.requiredGender;
  }

  /**
   * Get the database relationship type for a given relationship type
   * Maps high-level relationship types to database schema types
   */
  public getDatabaseRelationshipType(relationshipType: RelationshipType): 'parent_child' | 'spouse' {
    // Siblings are represented as shared parent-child relationships
    if (relationshipType === 'brother' || relationshipType === 'sister') {
      return 'parent_child';
    }

    // Spouse is spouse
    if (relationshipType === 'spouse') {
      return 'spouse';
    }

    // All parent/child variants map to parent_child
    return 'parent_child';
  }

  // ==================== PHASE 2: DUAL PARENTAGE SUPPORT ====================

  /**
   * Get inherited parent IDs for sibling relationships (Phase 2)
   * When adding a brother/sister, new person should share source person's parents
   *
   * @param sourcePerson - The existing person whose parents will be inherited
   * @returns Object with father_id, mother_id, or parents_id
   */
  public getInheritedParentIds(sourcePerson: {
    father_id?: string | null;
    mother_id?: string | null;
    parents_id?: string | null;
  }): {
    father_id?: string;
    mother_id?: string;
    parents_id?: string;
  } {
    if (sourcePerson.parents_id) {
      // If source person has adoptive parents, new sibling shares that couple
      return { parents_id: sourcePerson.parents_id };
    }

    // Otherwise inherit biological parents
    return {
      father_id: sourcePerson.father_id || undefined,
      mother_id: sourcePerson.mother_id || undefined,
    };
  }

  /**
   * Validate dual parentage exclusivity (Phase 2)
   * Business rule: Cannot have both individual parents AND couple-based parents
   *
   * @param data - Parentage data to validate
   * @returns Validation result with error message if invalid
   */
  public validateDualParentage(data: {
    father_id?: string | null;
    mother_id?: string | null;
    parents_id?: string | null;
  }): { valid: boolean; error?: string } {
    if (data.parents_id && (data.father_id || data.mother_id)) {
      return {
        valid: false,
        error: 'Cannot have both parents_id and individual father_id/mother_id',
      };
    }
    return { valid: true };
  }

  /**
   * Determine sibling type based on shared parentage (Phase 2)
   * Classifies siblings as full, half, or step based on shared parents
   *
   * @param person1 - First person with parentage fields
   * @param person2 - Second person with parentage fields
   * @returns Sibling type classification
   */
  public determineSiblingType(
    person1: {
      father_id?: string | null;
      mother_id?: string | null;
      parents_id?: string | null;
    },
    person2: {
      father_id?: string | null;
      mother_id?: string | null;
      parents_id?: string | null;
    }
  ): 'full' | 'half' | 'step' {
    const sharedFather = person1.father_id && person1.father_id === person2.father_id;
    const sharedMother = person1.mother_id && person1.mother_id === person2.mother_id;
    const sharedParents = person1.parents_id && person1.parents_id === person2.parents_id;

    if (sharedParents || (sharedFather && sharedMother)) {
      return 'full'; // Full siblings (share both parents or same adoptive couple)
    } else if (sharedFather || sharedMother) {
      return 'half'; // Half siblings (share exactly one biological parent)
    } else {
      return 'step'; // Step-siblings (no shared biological parents)
    }
  }
}

// ==================== SINGLETON INSTANCE ====================

/**
 * Singleton instance of RelationshipDomainService
 * Use this throughout the application for consistency
 */
export const relationshipDomainService = new RelationshipDomainService();
